import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import SubNav from './SubNav';
import MobileNavMenu from './MobileNavMenu';
import AnonMenu from './AnonMenu/AnonMenu';
import Hamburger from './Hamburger';
import { useRouter } from 'next/router';
import { Logo, LogoBrand, Button } from 'edcom-ui';
import AccountContext from '../../services/AccountContext';
import dynamic from 'next/dynamic';
import BannerNotifications from './BannerNotifications/BannerNotifications';
import styles from './TopNav.module.less';
import clsx from 'clsx';
import BrainzyAccess from './BrainzyAccess';
import { SearchBar } from '../../edcom-ui';
import { tabInfoPropType } from './util';
import { replaceParams } from '../../services/content';
import { pushAppRoute } from '../links/AppLink';

// Code split this into a separate chunk
const UserMenu = dynamic(() => import('./MemberMenu/UserMenu'), { ssr: false });

export default function TopNav({
  page,
  defaultTopNav = true,
  navInfo,
  searchMetaTitle,
  query,
  urlPath,
}) {
  //Determine if user is a member or not after AccountContext returns user object
  const { email, ready, isPremium, isSchoolAdmin } = useContext(AccountContext);
  const isMember = email != null;
  const [isMobileNavMenuOpen, setIsMobileNavMenuOpen] = useState(false);
  const router = useRouter();

  // Close the MobileNavMenu when navigation occurs
  useEffect(() => {
    const callback = () => setIsMobileNavMenuOpen(false);

    router.events.on('routeChangeStart', callback);
    return () => router.events.off('routeChangeStart', callback);
  }, []);

  /**
   * Handle search in searchBar.
   *
   * @param {string} term the search term
   * @param {boolean} searchWithin True if this is a search generated from searchWithin suggestion
   * @param {boolean} directSearch True if this is a search directly from search form
   * @param {boolean} termMatchSuggestion True is this is a search generated from searchWithin suggestion
   * and the term user searched matches one of the suggestions.
   */
  const handleSearch = async (
    term,
    searchWithin,
    directSearch,
    termMatchSuggestion
  ) => {
    let newUrl = '';

    if (searchWithin) {
      // When selected searchWithIn and the term is a suggestion
      if (termMatchSuggestion) {
        newUrl = `${urlPath}${term}/`;
      } else {
        // search when selected searchWithIn is not a direct suggestion
        newUrl = replaceParams(urlPath, { q: term });
      }
    } else {
      // Direct search
      // If direct search matches a suggestion then go directly to that URL, i.e. /resources/suggection/
      newUrl = termMatchSuggestion
        ? `/resources/${term}/`
        : replaceParams('/resources/', { q: term });
    }

    await pushAppRoute(router, newUrl);
  };

  // EDENG-2704: add a skip-to-content button for accessibility
  const skipToContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('maincontent');
    if (mainContent) {
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      mainContent.addEventListener('blur', function blurListener() {
        mainContent.removeAttribute('tabindex');
        mainContent.removeEventListener('blur', blurListener);
      });
    }
  };

  return (
    defaultTopNav && (
      <>
        {/* todo: my accounts modal, probably move to Login component */}
        <header className={styles.header}>
          <a
            href="#maincontent"
            className={styles.skipToMainContent}
            onClick={skipToContent}
          >
            SKIP TO CONTENT
          </a>
          <div className={styles.container}>
            <div className={styles.navLinks}>
              <div className={clsx(styles.logoWrapper)}>
                <a
                  href="/"
                  title="Education.com Home Page"
                  aria-label="logo"
                  className={styles.logoLink}
                >
                  <Logo className={clsx(styles.logo)} />
                  <div
                    className={clsx(
                      styles.whiteCover,
                      isMobileNavMenuOpen && styles.openCover
                    )}
                  ></div>
                  <LogoBrand className={clsx(styles.logoBrand)} />
                </a>
              </div>
              <div
                className={clsx(
                  isMobileNavMenuOpen
                    ? styles.visibleOverlay
                    : styles.hiddenOverlay
                )}
              >
                <div></div>
              </div>
              <MobileNavMenu
                navInfo={navInfo}
                isOpen={isMobileNavMenuOpen}
                setIsOpen={setIsMobileNavMenuOpen}
              />
            </div>
            <div className={styles.searchBarContainer}>
              <SearchBar
                placeholder="Search our library"
                cid="11.75"
                page={page}
                showDropdown={true}
                onSubmit={handleSearch}
                searchMetaTitle={searchMetaTitle}
                query={query}
                urlPath={urlPath}
              />
            </div>
            <div className={styles.brainzyAccount}>
              {ready && isMember && <BrainzyAccess isMobile={false} />}
              {(ready || isMember) && (
                <motion.div
                  className={styles.userLinks}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  {isMember && !isPremium && !isSchoolAdmin && (
                    <div
                      className={clsx(
                        styles.displayTryPremium,
                        isMobileNavMenuOpen && styles.fadeIn
                      )}
                    >
                      <Button
                        href={'/pricing/'}
                        color={'dark-teal'}
                        className={styles.tryPremium}
                      >
                        Try Premium
                      </Button>
                    </div>
                  )}
                  <div
                    className={clsx(
                      styles.userLinkHamburger,
                      isMember && isMobileNavMenuOpen && styles.hideIcon
                    )}
                  >
                    {isMember ? (
                      <UserMenu />
                    ) : (
                      <AnonMenu mobileNavMenuOpen={isMobileNavMenuOpen} />
                    )}
                  </div>
                </motion.div>
              )}
              <Hamburger open={isMobileNavMenuOpen} />
            </div>
          </div>
        </header>
        <div className={styles.hideTopNav}>
          <SubNav navInfo={navInfo} />
        </div>
        <BannerNotifications />
      </>
    )
  );
}

TopNav.propTypes = {
  page: PropTypes.string,
  defaultTopNav: PropTypes.bool,
  searchMetaTitle: PropTypes.string,
  query: PropTypes.string,
  urlPath: PropTypes.string,
  navInfo: PropTypes.arrayOf(tabInfoPropType),
};
